import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Snackbar,
  Paper,
  Typography,
  Button,
  IconButton,
  Slide,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import GetAppIcon from "@material-ui/icons/GetApp";
import IosShareIcon from "@material-ui/icons/OpenInNew";
import isEquipeChatMobileApp from "../../utils/isEquipeChatMobileApp";

const DISMISSED_KEY = "pwa-install-dismissed-v1";
const DISMISSED_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 dias

function isIOS() {
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function isInStandaloneMode() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isDismissed() {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return false;
    const { at } = JSON.parse(raw);
    return Date.now() - at < DISMISSED_TTL_MS;
  } catch {
    return false;
  }
}

function saveDismissed() {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify({ at: Date.now() }));
  } catch {
    // ignore
  }
}

const useStyles = makeStyles((theme) => ({
  paper: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 16px",
    borderRadius: "16px 16px 0 0",
    background: theme.palette.background.paper,
    boxShadow: "0 -4px 20px rgba(0,0,0,0.18)",
    width: "100%",
    boxSizing: "border-box",
  },
  icon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    objectFit: "cover",
    flexShrink: 0,
  },
  texts: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontWeight: 700,
    fontSize: 14,
    lineHeight: 1.3,
  },
  subtitle: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    marginTop: 2,
  },
  installBtn: {
    flexShrink: 0,
    fontWeight: 700,
    borderRadius: 8,
    textTransform: "none",
    fontSize: 13,
    padding: "6px 14px",
  },
  closeBtn: {
    padding: 4,
    flexShrink: 0,
  },
  snackbar: {
    bottom: 0,
    left: 0,
    right: 0,
    transform: "none !important",
  },
  snackbarWithNav: {
    bottom: "calc(56px + env(safe-area-inset-bottom, 0px))",
  },
  iosHint: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    marginTop: 2,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
}));

function SlideUp(props) {
  return <Slide {...props} direction="up" />;
}

const PWAInstallPrompt = ({ hasMobileBottomNav = false }) => {
  const classes = useStyles();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const ios = isIOS();

  useEffect(() => {
    // Já rodando dentro de um app nativo (ou embutido num iframe de um) — não faz
    // sentido oferecer instalação. isInStandaloneMode() sozinho não cobre esse caso
    // porque display-mode/navigator.standalone só valem pro contexto de nível
    // superior, não pra dentro de um <iframe>.
    if (isEquipeChatMobileApp() || isInStandaloneMode() || isDismissed()) return;

    if (ios) {
      // iOS não dispara beforeinstallprompt; mostra banner de instrução
      setVisible(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [ios]);

  const handleDismiss = () => {
    setVisible(false);
    saveDismissed();
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
    }
    setDeferredPrompt(null);
  };

  if (!visible) return null;

  return (
    <Snackbar
      open={visible}
      TransitionComponent={SlideUp}
      className={`${classes.snackbar} ${hasMobileBottomNav ? classes.snackbarWithNav : ""}`}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Paper className={classes.paper} elevation={8}>
        <img
          src="/android-chrome-192x192.png"
          alt="Agilzap"
          className={classes.icon}
        />
        <div className={classes.texts}>
          <Typography className={classes.title}>Instalar Agilzap</Typography>
          {ios ? (
            <Typography className={classes.iosHint}>
              Toque em <IosShareIcon style={{ fontSize: 13, margin: "0 2px" }} />{" "}
              e depois &ldquo;Adicionar à Tela de Início&rdquo;
            </Typography>
          ) : (
            <Typography className={classes.subtitle}>
              Acesso rápido direto da tela inicial
            </Typography>
          )}
        </div>
        {!ios && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<GetAppIcon />}
            className={classes.installBtn}
            onClick={handleInstall}
          >
            Instalar
          </Button>
        )}
        <IconButton
          size="small"
          className={classes.closeBtn}
          onClick={handleDismiss}
          aria-label="fechar"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
    </Snackbar>
  );
};

export default PWAInstallPrompt;
